import { cloneElement, forwardRef, Fragment, HtmlHTMLAttributes, memo, ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  arrow as arrowFn,
  autoUpdate,
  ElementProps,
  flip,
  FloatingArrow,
  FloatingArrowProps,
  limitShift,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  UseFloatingOptions,
  UseFloatingReturn,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import classNames from "classnames";
import { animated, useTransition } from "react-spring";
import { useMergedRef, useRefEffect } from "../../utils";
import { useGetFloatingUIPortalElement } from "../FloatingUIPortal";

import style from "./style.module.scss";

export type TooltipInstance = UseFloatingReturn

export type TooltipProps = Omit<HtmlHTMLAttributes<HTMLDivElement>, "children" | "content"> & {
  content?: ReactNode,
  supportingContent?: ReactNode,
  children: ReactElement,
  arrow?: boolean,
  disabled?: boolean,
  animationDuration?: number,
  isOpen?: boolean,
  setIsOpen?: (isOpen: boolean) => void,
  contentContainerProps?: HtmlHTMLAttributes<HTMLDivElement>,
  supportingContentContainerProps?: HtmlHTMLAttributes<HTMLDivElement>,
  arrowProps?: FloatingArrowProps & HtmlHTMLAttributes<SVGElement>,
  delay?: number,

  placement?: Placement,
  interactions?: Array<ElementProps | void>,
  options?: UseFloatingOptions
}

export const Tooltip = memo(forwardRef<TooltipInstance, TooltipProps>((
  {
    content,
    supportingContent,
    className,
    children,
    isOpen,
    setIsOpen,
    arrowProps,
    contentContainerProps,
    supportingContentContainerProps,
    delay = 600,
    animationDuration = 300,
    arrow = true,
    disabled,
    placement,
    interactions,
    options,
    ...props
  }, ref) => {
  const [ internalIsOpen, setInternalIsOpen ] = useState(isOpen);
  const arrowRef = useRef(null);

  if (children.type === Fragment) {
    throw new Error("Don't use fragments inside [Tooltip] component");
  }

  const portalElement = useGetFloatingUIPortalElement();
  const instance = useFloating({
    placement,
    whileElementsMounted: autoUpdate,
    open: isOpen ?? internalIsOpen,
    onOpenChange: setIsOpen ?? setInternalIsOpen,
    ...options,
    middleware: [
      flip(),
      offset(10),
      shift({
        padding: 10,
        limiter: limitShift(),
      }),
      arrowFn({
        element: arrowRef,
      }),
      ...options?.middleware || [],
    ],
  });
  const { refs, floatingStyles, context, update } = instance;
  const hover = useHover(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions(interactions ?? [
    hover,
    dismiss,
  ]);

  useRefEffect(ref, instance);
  useEffect(() => { update?.(); }, [ content, supportingContent, update ]);

  const transitions = useTransition([ internalIsOpen, isOpen ],
    {
      from: { opacity: 0 },
      enter: { opacity: 1, delay },
      leave: { opacity: 0 },
      config: {
        duration: animationDuration,
      },
    },
  );
  const mergedRef = useMergedRef((children as any).ref, refs.setReference);

  if (!portalElement || disabled || (!content && !supportingContent)) {
    return children;
  }

  return (
    <>
      {cloneElement(children, {
        ref: mergedRef,
        ...getReferenceProps(),
      })}
      {(isOpen ?? internalIsOpen) &&
        createPortal(
          transitions((springProps, item) => item && (
            <animated.div
              className={classNames(style.tooltip, className)}
              ref={refs.setFloating}
              style={{ ...floatingStyles, ...springProps }}
              {...getFloatingProps()}
              {...props}
            >
              {
                !!content &&
                <div
                  key="content"
                  {...contentContainerProps}
                  className={classNames(style.contentContainer, contentContainerProps?.className)}
                >
                  {content}
                </div>
              }
              {
                !!supportingContent &&
                <div
                  key="supportingContent"
                  {...supportingContentContainerProps}
                  className={classNames(style.supportingContentContainer, supportingContentContainerProps?.className)}
                >
                  {supportingContent}
                </div>
              }
              {
                arrow &&
                <FloatingArrow
                  key="arrow"
                  width={12}
                  height={6}
                  tipRadius={2}
                  {...arrowProps}
                  context={context}
                  className={classNames(style.arrow, arrowProps?.className)}
                  ref={arrowRef}
                />
              }
            </animated.div>
          )),
          portalElement,
        )
      }
    </>
  );
}));


